import React from 'react';
import PropTypes from 'prop-types';
import { isEqual } from 'lodash';
import { isIPadSafari } from '../../common/utils/browserUtils';
import { ApolloContext } from '../../ApolloContextProvider';
import ASSESSMENT_CAPTIONS from '../../captions/captionsConstants/captions';
import { createDevLogger } from '../../common/utils/devLog';
import { ManagedHowl, Howl } from './ManagedHowl';

const logger = createDevLogger('TRCAudioView');

export default class TRCAudioView extends React.Component {
    constructor(props) {
        super(props);
        this.initPlaylist = this.initPlaylist.bind(this);
        this.startPlay = this.startPlay.bind(this);
        this.displaySubtitles = this.displaySubtitles.bind(this);
        this.state = {
            captionsContent: '',
            startedPlaying: false,
        };
    }

    componentDidMount() {
        this.initPlaylist(this.props);
    }

    componentDidUpdate(prevProps) {
        if (!isEqual(this.props.audioTracks, prevProps.audioTracks)) {
            this.initPlaylist(this.props);
        }
        if (this.props.isReadyToPlay) {
            if (prevProps.isPaused && !this.props.isPaused) {
                this.startPlay();
            } else if (!this.state.startedPlaying && !prevProps.isReadyToPlay) {
                this.setState({ startedPlaying: true }, () => {
                    clearTimeout(this.playTimeout);
                    this.playTimeout = setTimeout(this.startPlay, this.props.delay);
                });
            }
        }
        if (!prevProps.isPaused && this.props.isPaused) {
            this.pausePlay();
        }
    }

    componentWillUnmount() {
        this.destroyPlaylist();
    }

    isSoundPlaying() {
        // MOCK MODE: Check if we have a mock timer running
        if (this.props.mockAudioPlayback) {
            return this.playlist && this.playlist.length > 0 && this.state.startedPlaying;
        }
        return this.playlist.length > 0 && this.playlist[this.currentTrack].playing();
    }

    displaySubtitles(sound, audioTrack) {
        const allCaptionsForAssessment = ASSESSMENT_CAPTIONS[this.props.assessment.name];
        if (allCaptionsForAssessment === undefined) {
            return;
        }
        const captions = allCaptionsForAssessment[audioTrack];
        if (!captions) {
            return;
        }
        // If the sound is playing, find in which second it is currently playing
        // Otherwise exit from a function
        if (!this.isSoundPlaying()) {
            return;
        }
        const currentTime = sound.seek();

        let subtitleToShow = null;
        // Find the subtitle that should be displayed at the current time
        if (captions.length) {
            for (let i = 0; i < captions.length; i += 1) {
                if (currentTime >= captions[i].start && currentTime < captions[i].end) {
                    subtitleToShow = captions[i];
                    break;
                }
            }
        }
        // Display the subtitle content in the captions container
        if (subtitleToShow) {
            if (subtitleToShow.content !== this.state.captionsContent) {
                this.setState({ captionsContent: subtitleToShow.content });
            }
        } else if (this.state.captionsContent !== '') {
            this.setState({ captionsContent: '' });
        }

        // Clear any existing timeout before setting a new one
        clearTimeout(this.subtitleTimeout);

        // Only continue if component is still mounted
        if (this.playlist) {
            this.subtitleTimeout = setTimeout(this.displaySubtitles, 100, sound, audioTrack);
        }
    }

    initPlaylist(props) {
        this.destroyPlaylist();
        if (props.audioTracks.length === 0) {
            return;
        }

        // MOCK MODE: Skip actual audio loading
        if (props.mockAudioPlayback) {
            logger.log('MOCK MODE: Simulating audio playback');
            this.playlist = props.audioTracks.map(() => ({ mock: true }));
            this.currentTrack = 0;
            return;
        }

        this.playlist = [];
        this.preloadStatuses = []; // Track if mp3 preloaded to skip unload on destroy (MAZE offline)
        this.currentTrack = 0;
        const playNextTrack = () => {
            this.playlist[this.currentTrack].stop(); // Reset to beginning for MAZE offline
            this.currentTrack += 1;
            if (this.currentTrack === this.playlist.length) {
                this.endPlay();
                if (props.onEndCallback) {
                    props.onEndCallback(this.currentTrack);
                }
                return;
            }
            setTimeout(() => this.playlist[this.currentTrack].play(), props.delay);
        };
        // Handle iPad Safari unlock for audio playback
        const handlePlayError = sound => {
            if (isIPadSafari()) {
                sound.once('unlock', () => sound.play());
            }
        };

        props.audioTracks.forEach((current, i) => {
            let sound;
            // Support audio caching for SEB browser offline mode (no service worker support)
            // Howl objects created on app load and stored in memory
            if (props.audioTracks[i] instanceof Howl) {
                sound = props.audioTracks[i];
                sound.on('end', playNextTrack);
                sound.on('play', () => {
                    if (props.onPlayCallback) {
                        props.onPlayCallback(this.currentTrack);
                    }
                    this.setState({ startedPlaying: true });
                    if (this.context.isCaptionsEnabled) {
                        this.displaySubtitles(sound, props.audioTracks[i]._src);
                    }
                });
                sound.on('loaderror', () => this.props.onAudioLoadingError());
                sound.on('playerror', () => handlePlayError(sound));
                this.preloadStatuses.push(true);
            } else {
                sound = new ManagedHowl({
                    src: props.audioTracks[i],
                    onend: playNextTrack,
                    onplay: () => {
                        if (props.onPlayCallback) {
                            props.onPlayCallback(this.currentTrack);
                        }
                        this.setState({ startedPlaying: true });
                        if (this.context.isCaptionsEnabled) {
                            this.displaySubtitles(sound, props.audioTracks[i]);
                        }
                    },
                    onloaderror: () => this.props.onAudioLoadingError(),
                    onplayerror: () => handlePlayError(sound),
                    buffer: true,
                    unloadOnEnd: true,
                });
                this.preloadStatuses.push(false);
            }
            this.playlist.push(sound);
        });
    }

    startPlay() {
        if (!this.playlist) {
            return;
        }

        // MOCK MODE: Simulate playback with 1-second delay per track
        if (this.props.mockAudioPlayback) {
            logger.log('MOCK MODE: Starting simulated playback');
            if (this.props.onPlayCallback) {
                this.props.onPlayCallback(this.currentTrack);
            }
            this.setState({ startedPlaying: true });

            const playNextMockTrack = () => {
                this.currentTrack += 1;
                if (this.currentTrack === this.playlist.length) {
                    logger.log('MOCK MODE: All tracks completed');
                    this.endPlay();
                    if (this.props.onEndCallback) {
                        this.props.onEndCallback(this.currentTrack);
                    }
                    return;
                }
                logger.log('MOCK MODE: Playing next track', this.currentTrack);
                if (this.props.onPlayCallback) {
                    this.props.onPlayCallback(this.currentTrack);
                }
                this.mockPlaybackTimer = setTimeout(playNextMockTrack, 1000);
            };

            this.mockPlaybackTimer = setTimeout(playNextMockTrack, 1000);
            return;
        }

        this.playlist[0].play();
    }

    pausePlay() {
        if (this.playlist) {
            this.playlist[this.currentTrack].pause();
        }
    }

    endPlay() {
        this.currentTrack = 0;
        this.props.stopAudio();
        this.setState({ captionsContent: '' });
    }

    destroyPlaylist() {
        if (this.mockPlaybackTimer) {
            clearTimeout(this.mockPlaybackTimer);
            this.mockPlaybackTimer = null;
        }

        if (this.playlist) {
            if (!this.props.mockAudioPlayback) {
                this.playlist.forEach((_, i) => {
                    this.playlist[i].off();
                    this.playlist[i].stop();
                    if (!this.preloadStatuses[i]) {
                        this.playlist[i].unload();
                    }
                    this.playlist[i] = null;
                });
                this.preloadStatuses = null;
            }
            this.playlist = null;
            this.currentTrack = 0;
            this.setState({ startedPlaying: false });
        }
        clearTimeout(this.subtitleTimeout);
        clearTimeout(this.playTimeout);
    }

    render() {
        return this.props.isReadyToPlay &&
            !this.props.isPaused &&
            this.context.isCaptionsEnabled &&
            this.state.captionsContent !== '' ? (
            <div className="captions-container">
                <span className="captions">{this.state.captionsContent}</span>
            </div>
        ) : null;
    }
}

TRCAudioView.propTypes = {
    stopAudio: PropTypes.func,
    onAudioLoadingError: PropTypes.func,
    audioTracks: PropTypes.array.isRequired,
    delay: PropTypes.number,
    isReadyToPlay: PropTypes.bool.isRequired,
    isPaused: PropTypes.bool,
    onPlayCallback: PropTypes.func,
    onEndCallback: PropTypes.func,
    assessment: PropTypes.object,
    mockAudioPlayback: PropTypes.bool, // TESTING FLAG: Set to true to mock audio playback with 1-second delays
};

TRCAudioView.defaultProps = {
    onAudioLoadingError: () => {},
    delay: 0,
    isPaused: false,
    onPlayCallback: undefined,
    onEndCallback: undefined,
    stopAudio: () => {},
    assessment: {},
    mockAudioPlayback: false,
};

TRCAudioView.contextType = ApolloContext;
